import _ from 'lodash';
import {connect} from 'react-redux';

import {globalNamespace} from '../../../../libs/shared/core/src/models/helpers/namespaceHelpers';
import {ResolveStateProps} from '../../../../libs/shared/core/src/store/helpers/commonReselectHelpers';
import {ResolveDispatchProps} from '../../../../libs/shared/core/src/store/helpers/reduxHelpers';
import {ContactDetailsPanelId} from '../../../../libs/shared/core/src/store/panels/panelStateBase';
import {
  createSelector,
  makeMapDispatchToProps,
  makeMapStateToProps,
} from '../../../../srcCommon/helpers/reselectHelpers';
import {CardModel, CardTypesEnum} from '../../../models/api/cardModel';
import {VisualContactModel} from '../../../models/api/visualContactModel';
import {duplicateCardAsyncAction, updateCardAsyncAction} from '../../../store/data/cards/cardsActions';
import {getCards} from '../../../store/data/dataSelectors';
import {getUserNamespace} from '../../../store/data/teammates/teammatesSelectors';
import {
  convertToGlobalContactWithErrorBannerAsyncAction,
  createCardAndUpdateContactDetailsPanelAction,
} from '../../../store/panels/contacts/contactDetailsPanelActions';
import {makeGetContactDetailsPanel} from '../../../store/panels/contacts/contactDetailsPanelSelectors';
import {ContactHeaderVisibilityIndicator} from './contactHeaderVisibilityIndicator';

/*
 * Props.
 */

interface ContactHeaderProps {
  panelId: ContactDetailsPanelId;
  contact?: VisualContactModel;
  card: CardModel | undefined;
}

/*
 * Selectors.
 */

function makeGetAdditionalContactCards(
  getContactDetailsPanel: ReturnType<typeof makeGetContactDetailsPanel>,
) {
  return createSelector([getContactDetailsPanel, getCards], (panel, cards) => {
    if (!panel) {
      return [];
    }

    return _(panel.cardIds)
      .filter((cardId) => cardId !== panel.selectedCardId)
      .map((cardId) => cards[cardId])
      .compact()
      .value();
  });
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<ContactHeaderProps>()(() => {
  const getContactDetailsPanel = makeGetContactDetailsPanel();
  const getAdditionalCards = makeGetAdditionalContactCards(getContactDetailsPanel);
  return {
    userNamespace: getUserNamespace,
    additionalCards: getAdditionalCards,
  };
});

const mapDispatchToProps = makeMapDispatchToProps()(() => ({
  updateCardAsyncAction,
  duplicateCardAsync: duplicateCardAsyncAction,
  createCardAndUpdateContactDetailsPanel: createCardAndUpdateContactDetailsPanelAction,
  convertToGlobalContact: convertToGlobalContactWithErrorBannerAsyncAction,
}));

function mergeProps(
  stateProps: ResolveStateProps<typeof mapStateToProps>,
  dispatchProps: ResolveDispatchProps<typeof mapDispatchToProps>,
  ownProps: ContactHeaderProps,
) {
  const {userNamespace} = stateProps;
  const {card, panelId, contact} = ownProps;

  const addToSharedContacts = async () => {
    // If we do not have a card, we need to create the company card
    if (!card) {
      // Return since we cant create a card without a contact.
      if (!contact) {
        return undefined;
      }
      const newCard = await dispatchProps.createCardAndUpdateContactDetailsPanel(panelId, {
        namespace: globalNamespace,
        name: contact.name || contact.contact.handle,
        contacts: [contact.contact],
      });
      if (!newCard) {
        return undefined;
      }

      return newCard;
    }

    await dispatchProps.updateCardAsyncAction(card.url, {type: CardTypesEnum.ENHANCED});

    return card;
  };

  const addToPrivateContacts = async () => {
    // If we do not have a card, we need to create the private card
    if (!card) {
      // Return since we cant create a card without a contact.
      if (!contact) {
        return undefined;
      }
      const newCard = await dispatchProps.createCardAndUpdateContactDetailsPanel(panelId, {
        namespace: userNamespace,
        name: contact.name || contact.contact.handle,
        contacts: [contact.contact],
      });
      if (!newCard) {
        return undefined;
      }

      return newCard;
    }
    const duplicatedCard = await dispatchProps.duplicateCardAsync(card, {
      namespace: userNamespace,
    });

    return duplicatedCard;
  };

  const convertToGlobalContact = async () => {
    if (!card) {
      return;
    }
    await dispatchProps.convertToGlobalContact(panelId, card);
  };

  return {
    ...stateProps,
    ...ownProps,
    addToSharedContacts,
    addToPrivateContacts,
    convertToGlobalContact,
  };
}

export const ContactHeaderVisibilityIndicatorContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps,
)(ContactHeaderVisibilityIndicator);
